import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { MODULE_OPTIONS } from "@/lib/constants/issue-constants";
import type { Issue } from "@shared/schema";

// Zod schema for Quick Capture form (simplified version of full issue schema)
const quickCaptureSchema = z.object({
  title: z.string().min(1, "Title is required").max(255, "Title must be less than 255 characters"),
  description: z.string().optional(),
  severity: z.enum(["critical", "high", "medium", "low"], {
    required_error: "Severity is required",
  }),
  module: z.string().min(1, "Module is required"),
  environment: z.enum(["local", "ci", "staging", "production"], {
    required_error: "Environment is required",
  }),
  sprintId: z.string().optional(), // Accepts "__none__" or valid sprint ID
});

type QuickCaptureFormData = z.infer<typeof quickCaptureSchema>;

interface IssueQuickCaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultSprintId?: string;
}

export function IssueQuickCaptureModal({ open, onOpenChange, defaultSprintId }: IssueQuickCaptureModalProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch sprints for dropdown
  const { data: sprints } = useQuery<Array<{ id: string; sprintNumber: string; sprintName: string | null }>>({
    queryKey: ["/api/project-manager/sprints"],
  });

  const form = useForm<QuickCaptureFormData>({
    resolver: zodResolver(quickCaptureSchema),
    defaultValues: {
      title: "",
      description: "",
      severity: "medium",
      module: "Client Manager",
      environment: "local",
      sprintId: defaultSprintId || "__none__",
    },
  });

  const createIssueMutation = useMutation({
    mutationFn: async (data: QuickCaptureFormData) => {
      const response = await apiRequest("POST", "/api/crm/issues", data);
      return await response.json() as Issue;
    },
    onSuccess: (newIssue) => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/issues"] });
      toast({
        title: "Issue created",
        description: `Issue ${newIssue.issueNumber} has been created successfully.`,
      });
      form.reset();
      onOpenChange(false);
      // Navigate to the newly created issue detail page
      navigate(`/issues/${newIssue.id}`);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error creating issue",
        description: error.message || "Failed to create issue. Please try again.",
      });
    },
  });

  const onSubmit = async (data: QuickCaptureFormData) => {
    setIsSubmitting(true);
    try {
      // Convert "__none__" placeholder to undefined for optional sprint field
      const submitData = {
        ...data,
        sprintId: data.sprintId === "__none__" || !data.sprintId ? undefined : data.sprintId
      };
      await createIssueMutation.mutateAsync(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isSubmitting) {
      form.reset();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Report Issue</DialogTitle>
          <DialogDescription>
            Create a new issue to track bugs, errors, or improvements.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Title Field */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Brief description of the issue"
                      {...field}
                      data-testid="input-issue-title"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description Field */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Detailed description of the issue..."
                      className="min-h-[80px]"
                      {...field}
                      data-testid="textarea-issue-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Severity Field */}
            <FormField
              control={form.control}
              name="severity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Severity</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger data-testid="select-issue-severity">
                        <SelectValue placeholder="Select severity" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Module Field */}
            <FormField
              control={form.control}
              name="module"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Module</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger data-testid="select-issue-module">
                        <SelectValue placeholder="Select module" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MODULE_OPTIONS.map((module) => (
                        <SelectItem key={module} value={module}>
                          {module}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Environment Field */}
            <FormField
              control={form.control}
              name="environment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Environment</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger data-testid="select-issue-environment">
                        <SelectValue placeholder="Select environment" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="local">Local</SelectItem>
                      <SelectItem value="ci">CI</SelectItem>
                      <SelectItem value="staging">Staging</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Sprint Field (Optional) */}
            <FormField
              control={form.control}
              name="sprintId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sprint (Optional)</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger data-testid="select-issue-sprint">
                        <SelectValue placeholder="Link to sprint (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {sprints?.map((sprint) => (
                        <SelectItem key={sprint.id} value={sprint.sprintNumber}>
                          {sprint.sprintNumber}
                          {sprint.sprintName && ` - ${sprint.sprintName}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="button-create-issue"
              >
                {isSubmitting ? "Creating..." : "Create Issue"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
